// Retry identity is scoped to the authenticated user and exact command body.
// Failed/ambiguous requests retain the key; a definitive response releases it.
const ownerPaths = new Set([
  '/v1/onboarding/bootstrap',
  '/v1/organization/existing-clinics/adopt',
  '/v1/organization/clinic-managers/grant',
  '/v1/organization/clinic-managers/revoke',
  '/v1/organization/clinic-managers/reassign',
  '/v1/organization/clinic-manager-invitations',
  '/v1/organization/clinic-manager-invitations/revoke',
  '/v1/clinic-manager-invitations/claim',
  '/v1/organization/clinic-onboarding/confirm',
])

type RetryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

async function scopeDigest(value: string) {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('')
}

export function createAuthorizationCommandKeys(
  makeKey: () => string = () => crypto.randomUUID(),
  storage: RetryStorage | undefined = typeof window === 'undefined' ? undefined : window.sessionStorage,
  digest = scopeDigest,
) {
  const pending = new Map<string, string>()
  return {
    async acquire(uid: string | undefined, path: string, init: RequestInit) {
      if (!uid || init.method !== 'POST' || !ownerPaths.has(path)) return undefined
      // Persist only an opaque digest and retry key, never tokens, email or body.
      // Session storage survives reload without sharing another user's command.
      const scope = 'flio.authorization-retry.v1.' + await digest(JSON.stringify([uid, path, init.body ?? '']))
      let key = storage?.getItem(scope) ?? pending.get(scope)
      if (!key) {
        key = makeKey()
        // Storage errors fail before dispatch; an uncertain retry must not silently
        // receive a new identity because browser persistence is unavailable.
        storage?.setItem(scope, key)
        pending.set(scope, key)
      }
      return { key, release: () => {
        if (storage?.getItem(scope) === key) storage.removeItem(scope)
        if (pending.get(scope) === key) pending.delete(scope)
      } }
    },
  }
}
